import { AlertCircle, AlertTriangle, RefreshCw, ShieldCheck, Wrench } from 'lucide-react'
import { useEffect, useRef } from 'react'

import type { CheckoutErrorType } from './checkout-error'

interface CheckoutErrorCardProps {
  /** Message renvoyé par l'API, déjà rédigé pour le client. */
  message?: string
  type: CheckoutErrorType
}

const FALLBACK_MESSAGE =
  "Le paiement n'a pas pu être lancé. Aucun montant n'a été débité et votre réservation est conservée."

const TITLES: Record<CheckoutErrorType, string> = {
  error: "Le paiement n'a pas pu être lancé",
  maintenance: 'Paiement momentanément indisponible',
  rejected: 'Demande refusée par le prestataire',
}

/**
 * Erreur affichée en ligne dans le tunnel de paiement, dans le même registre
 * visuel que FlightErrorCard côté résultats de vols. Volontairement inline
 * plutôt qu'en boundary d'erreur : l'écran de paiement contient une saisie
 * (adresse de facturation, coordonnées) qu'on ne veut pas perdre.
 */
export function CheckoutErrorCard({ message, type }: CheckoutErrorCardProps) {
  const Icon = type === 'maintenance' ? Wrench : type === 'rejected' ? AlertTriangle : AlertCircle
  const cardRef = useRef<HTMLDivElement>(null)

  // Le bouton « Payer » est en bas d'un formulaire long : sans ce scroll,
  // l'erreur peut apparaître hors écran et passer inaperçue.
  useEffect(() => {
    cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [type, message])

  return (
    <div
      className="flex w-full flex-col gap-3 rounded-2xl border border-danger/30 bg-danger-subtle p-5"
      ref={cardRef}
      role="alert"
    >
      <div className="flex items-start gap-3">
        <div
          aria-hidden
          className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white"
        >
          <Icon className="h-4 w-4 text-danger" />
        </div>
        <div className="flex flex-col gap-1">
          <div className="text-base font-semibold leading-none text-danger-foreground">{TITLES[type]}</div>
          <p className="text-sm leading-relaxed text-danger-foreground">{message ?? FALLBACK_MESSAGE}</p>
        </div>
      </div>

      <ul className="ml-11 space-y-2 text-sm text-danger-foreground">
        {type === 'maintenance' ? (
          <>
            <li className="flex items-start gap-2">
              <RefreshCw className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Relancez le paiement dans quelques minutes : vos informations sont conservées
              </span>
            </li>
            <li className="flex items-start gap-2">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Le service est en cours de rétablissement, aucune autre action n&apos;est requise de
                votre part
              </span>
            </li>
          </>
        ) : type === 'rejected' ? (
          <li className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Vérifiez votre numéro de téléphone et votre adresse de facturation, puis relancez le
              paiement
            </span>
          </li>
        ) : (
          <>
            <li className="flex items-start gap-2">
              <RefreshCw className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Réessayez dans quelques instants</span>
            </li>
            <li className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Si le problème persiste, contactez notre équipe</span>
            </li>
          </>
        )}
      </ul>
    </div>
  )
}
